import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface AdvancedMarkerProps {
    map: google.maps.Map | null;
    position: google.maps.LatLngLiteral | google.maps.LatLng;
    children?: React.ReactNode;
    zIndex?: number;
    rotation?: number; // CSS rotation in degrees
    className?: string;
}

export const AdvancedMarker: React.FC<AdvancedMarkerProps> = ({
    map,
    position,
    children,
    zIndex,
    rotation = 0,
    className = "",
}) => {
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Initialize container only once if children exist
    if (!containerRef.current && children) {
        containerRef.current = document.createElement("div");
    }

    useEffect(() => {
        if (!map) return;

        if (!markerRef.current) {
            markerRef.current = new google.maps.marker.AdvancedMarkerElement({
                map,
                position,
                content: containerRef.current, // Pass the DOM element
                zIndex: zIndex,
            });
        }

        return () => {
            if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
            }
        };
    }, [map, position, zIndex]);

    useEffect(() => {
        if (markerRef.current) {
            markerRef.current.position = position;
        }
    }, [position]);

    useEffect(() => {
        if (markerRef.current && zIndex !== undefined) {
            markerRef.current.zIndex = zIndex;
        }
    }, [zIndex]);

    useEffect(() => {
        if (containerRef.current) {
            // Apply rotation to the container DIV
            // We typically want to rotate around the center, so ensure the content is centered
            containerRef.current.style.transform = `rotate(${rotation}deg)`;
            containerRef.current.style.transition = 'transform 0.1s linear'; // Smooth rotation
            if (className) containerRef.current.className = className;
        }
    }, [rotation, className]);

    if (!containerRef.current) return null;

    return createPortal(children, containerRef.current);
};
